import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ParentStorableData} from 'constants/SampleConstants';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import ChangeParentStorableButton from 'samples/storables/ChangeParentStorableButton';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {LocationGraphQL} from '../../../graphql-fragments/inventory/__generated/LocationGraphQL';
import {AutomationOutputFileConfigGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileConfigGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import waitForEntrySaved from '../utils/waitForEntrySaved';
import {LocationQuery, LocationQueryVariables} from './__generated/LocationQuery';
import {StorableSchemaQuery, StorableSchemaQueryVariables} from './__generated/StorableSchemaQuery';
import * as AutomationFileDAO from './AutomationFileDAO';
import {AutomationOutputFileConfig} from './LabAutomationConstants';

export const STORABLE_SCHEMA_QUERY = gql`
  query StorableSchemaQuery($storableSchemaId: StrictString!) @skipAudit @engComponent(name: LAB_AUTOMATION) {
    storableSchema(id: $storableSchemaId) {
      ... on StorableSchemaInterface {
        ...StorableSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('StorableSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function useStorableSchemaQuery(
  automationOutputFileConfig?: AutomationOutputFileConfigGraphQL | AutomationOutputFileConfig | null
): StorableSchema | null {
  const destinationInfo = automationOutputFileConfig?.destinationInfo;
  // automationOutputFileConfig coming from RunAutomationFileViewer may be AutomationOutputFileConfigGraphQL
  // or AutomationOutputFileConfig so we handle both scenarios here.
  const storableSchemaId = destinationInfo
    ? destinationInfo.hasOwnProperty('storableSchemaId')
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (destinationInfo as any).storableSchemaId
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (destinationInfo as any)?.storableSchema?.id
    : null;
  const storableSchemaResult = useSkippableBenchlingQuery<StorableSchemaQuery, StorableSchemaQueryVariables>(
    STORABLE_SCHEMA_QUERY,
    {
      variables: {
        storableSchemaId: storableSchemaId!,
      },
      skip: storableSchemaId == null,
    }
  );
  return storableSchemaResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(storableSchemaResult.data.storableSchema)
    ? graphQLToREST('StorableSchema', storableSchemaResult.data.storableSchema)
    : null;
}

export const LOCATION_QUERY = gql`
  query LocationQuery($locationId: StrictString!) @skipAudit @engComponent(name: LAB_AUTOMATION) {
    location(id: $locationId) {
      ... on LocationNode {
        ...LocationGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Location').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function useLocationQuery(locationId: string | null): LocationGraphQL | null {
  const locationResult = useSkippableBenchlingQuery<LocationQuery, LocationQueryVariables>(LOCATION_QUERY, {
    variables: {
      locationId: locationId!,
    },
    skip: locationId == null,
  });
  return locationResult.type === QueryResultType.SUCCESS && !isInaccessible(locationResult.data.location)
    ? locationResult.data.location
    : null;
}

interface AutomationOutputFileLocationPickerProps {
  runId: string | null;
  automationOutputFile: AutomationOutputFileGraphQL;
  locked: boolean;
  updateAutomationOutputFile: (automationOutputFile: AutomationOutputFileGraphQL) => void;
  storableSchema: StorableSchema | null;
  location: LocationGraphQL | null;
}

interface ConnectedProps {
  outputFileStatus: AutomationFileStatusGraphQL | null;
}

class AutomationOutputFileLocationPickerConnector extends DataConnector<
  AutomationOutputFileLocationPickerProps,
  ConnectedProps
> {
  enhanceProps(): ConnectedProps {
    return {
      outputFileStatus: this.props.automationOutputFile.id
        ? this.reduxState().ui.automationFileStatus[this.props.automationOutputFile.id]
        : null,
    };
  }
}

const AutomationOutputFileLocationPicker = (
  props: AutomationOutputFileLocationPickerProps & ConnectedProps
): JSX.Element | null => {
  const automationOutputFile = props.automationOutputFile;
  const locationIdFromFile = automationOutputFile.locationId || null;
  const locationIdFromStore = props.location?.id || null;
  const isLoading = locationIdFromFile !== locationIdFromStore;
  const buttonText = isLoading ? 'Loading...' : props.location ? props.location.name : 'Choose Location';
  const isWritable =
    !props.locked &&
    props.outputFileStatus !== AutomationFileStatusGraphQL.running &&
    automationOutputFile.status === AutomationFileStatusGraphQL.pending;
  return (
    <Row className="u-mini-spacing-top" bufferRowReset={true}>
      <Col md={3}>
        <span className="fieldLabel-sublabel">Location</span>
      </Col>
      <Col md={9}>
        <EditorPropertiesContext.Consumer>
          {({fileId}) => (
            <ChangeParentStorableButton
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              buttonSize={ButtonSize.MINI}
              className={css(locationPickerStyles.changeParentStorableButton)}
              icon={isLoading ? ModelTypeConstants.LOCATION : undefined}
              buttonText={buttonText}
              fullWidth={true}
              isWritable={isWritable}
              onSetParentStorable={async (data: ParentStorableData) => {
                const saved = fileId != null ? await waitForEntrySaved(fileId) : true;
                if (saved) {
                  const response = await AutomationFileDAO.updateAutomationOutputFileParams(
                    automationOutputFile.id,
                    {locationId: data.id!}
                  );
                  props.updateAutomationOutputFile(response.automationOutputFile);
                }
              }}
              storableSchema={props.storableSchema}
              titleText="Create new inventory in..."
              parentStorableTypesOverride={[ModelTypeConstants.LOCATION]}
            />
          )}
        </EditorPropertiesContext.Consumer>
      </Col>
    </Row>
  );
};

export const locationPickerStyles = StyleSheet.create({
  changeParentStorableButton: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export default AutomationOutputFileLocationPickerConnector.connect(AutomationOutputFileLocationPicker);
